import { zodResolver } from '@hookform/resolvers/zod';
import { t, validationError } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@repo/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { Input } from '@repo/ui/input';
import { PinInput, PinInputGroup, PinInputSlot } from '@repo/ui/pin-input';
import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { authClient, useSession } from '@/providers/session';

export const ZDisable2FAForm = z.object({
  totpCode: z.string().trim().optional(),
  backupCode: z.string().trim().optional(),
});

export type TDisable2FAForm = z.infer<typeof ZDisable2FAForm>;

export const DisableAuthenticatorAppDialog = () => {
  useLocale();

  const { refreshSession } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const [twoFactorDisableMethod, setTwoFactorDisableMethod] = useState<'totp' | 'backup'>('totp');

  const disable2FAForm = useForm<TDisable2FAForm>({
    defaultValues: {
      totpCode: '',
      backupCode: '',
    },
    resolver: zodResolver(ZDisable2FAForm, { error: validationError }),
  });

  const onCloseTwoFactorDisableDialog = () => {
    disable2FAForm.reset();

    setIsOpen(!isOpen);
  };

  const onToggleTwoFactorDisableMethodClick = () => {
    const method = twoFactorDisableMethod === 'totp' ? 'backup' : 'totp';

    if (method === 'totp') {
      disable2FAForm.setValue('backupCode', '');
    }

    if (method === 'backup') {
      disable2FAForm.setValue('totpCode', '');
    }

    setTwoFactorDisableMethod(method);
  };

  const { isSubmitting: isDisable2FASubmitting } = disable2FAForm.formState;

  const onDisable2FAFormSubmit = async ({ totpCode, backupCode }: TDisable2FAForm) => {
    try {
      await authClient.twoFactor.disable({ totpCode, backupCode });

      toast.success(t('auth.twoFactorAuthenticationDisabled'), {
        get description() {
          return t('auth.twoFactorAuthenticationHasBeenDisabledForYourAccount');
        },
      });

      flushSync(() => {
        onCloseTwoFactorDisableDialog();
      });

      await refreshSession();
    } catch (_err) {
      toast.error(t('auth.unableToDisableTwoFactorAuthentication'), {
        get description() {
          return t('auth.weWereUnableToDisableTwoFactorAuthenticationFor');
        },
      });
    }
  };

  return (
    <Dialog onOpenChange={onCloseTwoFactorDisableDialog} open={isOpen}>
      <DialogTrigger asChild={true}>
        <Button className="shrink-0" variant="destructive">
          {t('auth.disable2fa')}{' '}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('auth.disable2fa')}</DialogTitle>
          <DialogDescription>
            {t('auth.pleaseProvideATokenFromTheAuthenticatorOrA')}{' '}
          </DialogDescription>
        </DialogHeader>
        <Form {...disable2FAForm}>
          <form onSubmit={disable2FAForm.handleSubmit(onDisable2FAFormSubmit)}>
            <fieldset className="flex flex-col gap-y-4" disabled={isDisable2FASubmitting}>
              {twoFactorDisableMethod === 'totp' && (
                <FormField
                  control={disable2FAForm.control}
                  name="totpCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <PinInput {...field} maxLength={6} value={field.value ?? ''}>
                          {Array(6)
                            .fill(null)
                            .map((_, i) => (
                              <PinInputGroup key={i}>
                                <PinInputSlot index={i} />
                              </PinInputGroup>
                            ))}
                        </PinInput>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              {twoFactorDisableMethod === 'backup' && (
                <FormField
                  control={disable2FAForm.control}
                  name="backupCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.backupCode')}</FormLabel>
                      <FormControl>
                        <Input type="text" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <DialogFooter>
                <Button
                  onClick={onToggleTwoFactorDisableMethodClick}
                  type="button"
                  variant="secondary"
                >
                  {twoFactorDisableMethod === 'totp'
                    ? t('auth.useBackupCode')
                    : t('auth.useAuthenticator')}
                </Button>
                <Button loading={isDisable2FASubmitting} type="submit" variant="destructive">
                  {t('auth.disable2fa')}{' '}
                </Button>
              </DialogFooter>
            </fieldset>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
